import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDashboardOrdersTrend } from '../services/api';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell,
    BarChart, Bar,
} from 'recharts';
import { HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineShoppingCart, HiOutlineCalendar, HiOutlineRefresh, HiOutlineTrendingUp, HiOutlineTrendingDown, HiOutlineClock, HiOutlineCurrencyDollar, HiOutlineCog } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';

/* ─── helpers ───────────────────────────────────────────────── */

const fmt = (n) => (n ?? 0).toLocaleString();



const today = () => {
    const d = new Date();
    return d.toISOString().split('T')[0];
};

const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split('T')[0];
};

/* colour palette */
const STATUS_COLORS = {
    COMPLETED: '#10b981',   // emerald-500
    REJECTED: '#ef4444',     // red-500
};

const TYPE_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#14b8a6'];

const GRADIENT_COLORS = {
    COMPLETED: { start: '#34d399', end: '#10b981' },
    REJECTED: { start: '#f87171', end: '#ef4444' },
};

/* ─── date-range presets ────────────────────────────────────── */
const DATE_PRESETS = [
    { label: 'Today', from: today(), to: today(), groupBy: 'day' },
    { label: 'Last 7 days', from: daysAgo(7), to: today(), groupBy: 'day' },
    { label: 'Last 30 days', from: daysAgo(30), to: today(), groupBy: 'week' },
    { label: 'Last 90 days', from: daysAgo(90), to: today(), groupBy: 'week' },
    { label: 'Last 6 months', from: daysAgo(180), to: today(), groupBy: 'month' },
    { label: 'Last 12 months', from: daysAgo(365), to: today(), groupBy: 'month' },
];

/* ─── Custom tooltip ────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg px-4 py-3">
            <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">{label}</p>
            {payload.map((entry, i) => (
                <p key={i} className="text-sm" style={{ color: entry.color }}>
                    <span className="font-medium">{entry.name}:</span>{' '}
                    <span className="font-bold">{fmt(entry.value)}</span>
                </p>
            ))}
        </div>
    );
};

/* ─── Stat Card ─────────────────────────────────────────────── */
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, trendLabel, onClick }) => {
    const colorMap = {
        emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', ring: 'ring-emerald-100', iconBg: 'bg-emerald-100' },
        red: { bg: 'bg-red-50', text: 'text-red-600', ring: 'ring-red-100', iconBg: 'bg-red-100' },
        blue: { bg: 'bg-blue-50', text: 'text-blue-600', ring: 'ring-blue-100', iconBg: 'bg-blue-100' },
        amber: { bg: 'bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-100', iconBg: 'bg-amber-100' },
        purple: { bg: 'bg-purple-50', text: 'text-purple-600', ring: 'ring-purple-100', iconBg: 'bg-purple-100' },
        indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', ring: 'ring-indigo-100', iconBg: 'bg-indigo-100' },
    };
    const c = colorMap[color] || colorMap.blue;

    return (
        <div 
            onClick={onClick}
            className={`relative overflow-hidden rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 p-5 group ${onClick ? 'cursor-pointer hover:border-blue-200' : ''}`}>
            {/* Decorative gradient blob */}
            <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${c.iconBg} opacity-50 group-hover:opacity-70 transition-opacity duration-300`}></div>
            <div className="relative flex items-start justify-between">
                <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
                    <p className="text-3xl font-bold text-gray-900 tracking-tight">{value}</p>
                    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
                    {trend !== undefined && (
                        <div className={`flex items-center mt-2 text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {trend >= 0 ? <HiOutlineTrendingUp className="w-3.5 h-3.5 mr-1" /> : <HiOutlineTrendingDown className="w-3.5 h-3.5 mr-1" />}
                            {Math.abs(trend)}% {trendLabel || 'vs previous'}
                        </div>
                    )}
                </div>
                <div className={`w-11 h-11 rounded-xl ${c.iconBg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${c.text}`} />
                </div>
            </div>
        </div>
    );
};

/* ─── Main component ────────────────────────────────────────── */
const ManagementOverview = () => {
    const { accessToken } = useAuth();
    const navigate = useNavigate();

    /* state */
    const [activePreset, setActivePreset] = useState(0); // default: today
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [groupBy, setGroupBy] = useState('week');
    const [isCustom, setIsCustom] = useState(false);
    const [orderType, setOrderType] = useState('');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /* derive query params */
    const queryParams = useMemo(() => {
        if (isCustom) {
            return { from: customFrom, to: customTo, groupBy, type: orderType };
        }
        const preset = DATE_PRESETS[activePreset];
        return { from: preset.from, to: preset.to, groupBy: preset.groupBy, type: orderType };
    }, [activePreset, isCustom, customFrom, customTo, groupBy, orderType]);

    /* fetch */
    const fetchData = useCallback(async () => {
        if (!queryParams.from || !queryParams.to) return;
        setLoading(true);
        setError(null);
        try {
            const result = await getDashboardOrdersTrend(queryParams.from, queryParams.to, queryParams.groupBy, queryParams.type);
            setData(result);
        } catch (err) {
            console.error('Dashboard fetch error:', err);
            setError(err.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [queryParams]);

    useEffect(() => { fetchData(); }, [fetchData, accessToken]);

    /* ─── Process API Data ──────────────────────────────────── */
    const processed = useMemo(() => {
        if (!data) return null;

        // The API may return data in various shapes. Handle them:
        // Expected: { trend: [...], summary: {...} } or an array directly
        let trendItems = [];
        let summary = {};

        if (Array.isArray(data)) {
            trendItems = data;
        } else if (data.trend) {
            trendItems = data.trend;
            summary = data.summary || {};
        } else if (data.data) {
            trendItems = Array.isArray(data.data) ? data.data : [];
            summary = data.summary || data.meta || {};
        } else {
            // Maybe the whole response is the summary
            summary = data;
        }

        // Build chart data from trend items
        const chartData = trendItems.map((item) => {
            const byStatus = item.byStatus || {};
            const completed = Number(byStatus.COMPLETED ?? item.completed ?? item.COMPLETED ?? item.completedCount ?? 0);
            const rejected = Number(byStatus.REJECTED ?? item.rejected ?? item.REJECTED ?? item.rejectedCount ?? 0);
            const pending = Number(byStatus.PENDING ?? item.pending ?? item.PENDING ?? item.pendingCount ?? 0);
            const approved1 = Number(byStatus.APPROVED1 ?? item.approved1 ?? item.APPROVED1 ?? item.approved1Count ?? 0);
            const approved2 = Number(byStatus.APPROVED2 ?? item.approved2 ?? item.APPROVED2 ?? item.approved2Count ?? 0);
            const total = Number(item.totalOrders ?? (completed + rejected + pending + approved1 + approved2));
            return {
                period: item.period || item.date || item.label || item.week || item.month || item.day || '',
                completed,
                rejected,
                pending,
                approved1,
                approved2,
                total,
            };
        });

        // Aggregate totals
        const totalCompleted = Number(summary.totalCompleted
            ?? summary.completed
            ?? chartData.reduce((s, d) => s + d.completed, 0));
        const totalRejected = Number(summary.totalRejected
            ?? summary.rejected
            ?? chartData.reduce((s, d) => s + d.rejected, 0));
        const totalPending = Number(summary.totalPending 
            ?? summary.pending 
            ?? chartData.reduce((s, d) => s + d.pending, 0));
        const totalApproved1 = Number(summary.totalApproved1 
            ?? summary.approved1 
            ?? chartData.reduce((s, d) => s + d.approved1, 0));
        const totalApproved2 = Number(summary.totalApproved2 
            ?? summary.approved2 
            ?? chartData.reduce((s, d) => s + d.approved2, 0));
        const totalOrders = Number(summary.totalOrders
            ?? summary.total
            ?? chartData.reduce((s, d) => s + d.total, 0));

        // Item type breakdown (if available)
        let typeBreakdown = [];
        if (summary.byType || summary.itemTypes || data.byType || data.itemTypes) {
            const raw = summary.byType || summary.itemTypes || data.byType || data.itemTypes;
            if (Array.isArray(raw)) {
                typeBreakdown = raw.map((t) => ({
                    name: t.type || t.name || t.label || 'Unknown',
                    value: Number(t.count || t.total || t.value || 0),
                }));
            } else if (typeof raw === 'object') {
                typeBreakdown = Object.entries(raw).map(([k, v]) => ({
                    name: k,
                    value: typeof v === 'number' ? v : Number(v?.count || v?.total || 0),
                }));
            }
        }

        // If no type breakdown from API, try building from trend items
        if (typeBreakdown.length === 0 && trendItems.length > 0) {
            const typeMap = {};
            trendItems.forEach((item) => {
                if (item.byType && typeof item.byType === 'object') {
                    Object.entries(item.byType).forEach(([type, data]) => {
                        const count = Number(data?.count ?? data?.total ?? 0);
                        typeMap[type] = (typeMap[type] || 0) + count;
                    });
                } else if (item.types && typeof item.types === 'object') {
                    Object.entries(item.types).forEach(([type, count]) => {
                        typeMap[type] = (typeMap[type] || 0) + Number(count || 0);
                    });
                }
                // Also check for specific type fields
                ['epin', 'EPIN', 'E-PIN', 'scratch', 'Scratch', 'etopup', 'E-TopUp', 'ETOPUP'].forEach((key) => {
                    if (item[key] !== undefined) {
                        const normalizedKey = key.replace(/^e/i, 'E-').replace(/pin$/i, 'PIN').replace(/topup$/i, 'TopUp');
                        typeMap[normalizedKey] = (typeMap[normalizedKey] || 0) + Number(item[key]);
                    }
                });
            });
            
            const displayNames = {
                'SCRATCH_CARD': 'Scratch Card',
                'EPIN': 'E-PIN',
                'ETOPUP': 'E-TopUp'
            };
            
            typeBreakdown = Object.entries(typeMap).map(([name, value]) => ({ 
                name: displayNames[name] || name, 
                value 
            }));
        }

        // Status breakdown for donut
        const statusBreakdown = [
            { name: 'Completed', value: totalCompleted, color: STATUS_COLORS.COMPLETED },
            { name: 'Rejected', value: totalRejected, color: STATUS_COLORS.REJECTED },
        ].filter((s) => s.value > 0);

        // Completion rate
        const completionRate = totalOrders > 0 ? ((totalCompleted / totalOrders) * 100).toFixed(1) : 0;

        return {
            chartData,
            totalCompleted,
            totalRejected,
            totalPending,
            totalApproved1,
            totalApproved2,
            totalOrders,
            typeBreakdown,
            statusBreakdown,
            completionRate,
        };
    }, [data]);

    /* ─── Render helpers ────────────────────────────────────── */

    const renderSkeleton = () => (
        <div className="max-w-7xl mx-auto animate-pulse">
            {/* Header skeleton */}
            <div className="mb-8">
                <div className="h-8 w-48 bg-gray-200 rounded-lg mb-2"></div>
                <div className="h-4 w-80 bg-gray-100 rounded-lg"></div>
            </div>
            {/* Stats skeleton */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 h-32">
                        <div className="h-4 w-24 bg-gray-200 rounded mb-3"></div>
                        <div className="h-8 w-20 bg-gray-200 rounded mb-2"></div>
                        <div className="h-3 w-32 bg-gray-100 rounded"></div>
                    </div>
                ))}
            </div>
            {/* Chart skeleton */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 h-80 mb-6">
                <div className="h-5 w-40 bg-gray-200 rounded mb-4"></div>
                <div className="h-64 bg-gray-50 rounded-lg"></div>
            </div>
        </div>
    );

    if (loading && !data) return <div className="p-6">{renderSkeleton()}</div>;

    if (error) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                    <HiOutlineXCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-red-700 mb-1">Failed to load dashboard</h3>
                    <p className="text-sm text-red-600 mb-4">{error}</p>
                    <button onClick={fetchData} className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors">
                        <HiOutlineRefresh className="w-4 h-4 mr-2" /> Retry
                    </button>
                </div>
            </div>
        );
    }

    const d = processed || {
        chartData: [],
        totalCompleted: 0,
        totalRejected: 0,
        totalPending: 0,
        totalApproved1: 0,
        totalApproved2: 0,
        totalOrders: 0,
        typeBreakdown: [],
        statusBreakdown: [],
        completionRate: 0,
    };

    return (
        <div className="max-w-7xl mx-auto">
            {/* ─── Page Header ─────────────────────────────────── */}
            <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-1">Overview</h1>
                        <p className="text-gray-500 text-sm">Sales performance & order analytics dashboard</p>
                    </div>
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        className="inline-flex items-center px-4 py-2.5 bg-white border border-gray-200 text-sm font-medium text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm disabled:opacity-50"
                    >
                        <HiOutlineRefresh className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* ─── Date Range Controls ─────────────────────────── */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Preset buttons */}
                    <div className="flex flex-wrap gap-2">
                        {DATE_PRESETS.map((preset, i) => (
                            <button
                                key={i}
                                onClick={() => { setActivePreset(i); setIsCustom(false); }}
                                className={`px-3.5 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                                    !isCustom && activePreset === i
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-8 bg-gray-200"></div>

                    {/* Custom date */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <HiOutlineCalendar className="w-4 h-4 text-gray-400 hidden sm:block" />
                        <input
                            type="date"
                            value={isCustom ? customFrom : queryParams.from}
                            onChange={(e) => { setCustomFrom(e.target.value); setIsCustom(true); }}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <span className="text-gray-400 text-sm">to</span>
                        <input
                            type="date"
                            value={isCustom ? customTo : queryParams.to}
                            onChange={(e) => { setCustomTo(e.target.value); setIsCustom(true); }}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {isCustom && (
                            <select
                                value={groupBy}
                                onChange={(e) => setGroupBy(e.target.value)}
                                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="day">Daily</option>
                                <option value="week">Weekly</option>
                                <option value="month">Monthly</option>
                            </select>
                        )}
                    </div>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-8 bg-gray-200"></div>

                    {/* Order Type Filter */}
                    <div className="flex items-center gap-2">
                        <select
                            value={orderType}
                            onChange={(e) => setOrderType(e.target.value)}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Types</option>
                            <option value="SCRATCH_CARD">Scratch Card</option>
                            <option value="EPIN">E-PIN</option>
                            <option value="ETOPUP">E-TopUp</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* ─── Summary Stats ───────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                <StatCard
                    title="Total Orders"
                    value={fmt(d.totalOrders)}
                    subtitle={`${queryParams.from} – ${queryParams.to}`}
                    icon={HiOutlineShoppingCart}
                    color="blue"
                />
                <StatCard
                    title="Completed"
                    value={fmt(d.totalCompleted)}
                    subtitle={`${d.completionRate}% completion rate`}
                    icon={HiOutlineCheckCircle}
                    color="emerald"
                />
                <StatCard
                    title="Rejected"
                    value={fmt(d.totalRejected)}
                    subtitle={d.totalOrders > 0 ? `${((d.totalRejected / d.totalOrders) * 100).toFixed(1)}% rejection rate` : 'No orders'}
                    icon={HiOutlineXCircle}
                    color="red"
                />
                <StatCard
                    title="Completion Rate"
                    value={`${d.completionRate}%`}
                    subtitle="Orders fulfilled successfully"
                    icon={HiOutlineTrendingUp}
                    color="amber"
                />
            </div>

            {/* ─── Status Stats ───────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
                <StatCard
                    title="Pending Orders"
                    value={fmt(d.totalPending)}
                    subtitle="Click to view pending orders"
                    icon={HiOutlineClock}
                    color="amber"
                    onClick={() => navigate('/management/pending-orders')}
                />
                <StatCard
                    title="Approved 1 (Money Deposit)"
                    value={fmt(d.totalApproved1)}
                    subtitle="Click to view money deposit"
                    icon={HiOutlineCurrencyDollar}
                    color="indigo"
                    onClick={() => navigate('/management/money-deposit')}
                />
                <StatCard
                    title="Approved 2 (Preparing)"
                    value={fmt(d.totalApproved2)}
                    subtitle="Click to view preparing orders"
                    icon={HiOutlineCog}
                    color="purple"
                    onClick={() => navigate('/management/preparing-orders')}
                />
            </div>

            {/* ─── Charts Row ──────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Trend Area Chart (2/3 width) */}
                <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">Order Trend</h2>
                            <p className="text-xs text-gray-400 mt-0.5">Completed vs Rejected over time</p>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                            <span className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Completed
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span> Rejected
                            </span>
                        </div>
                    </div>
                    {d.chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={d.chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                <defs>
                                    <linearGradient id="gradCompleted" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={GRADIENT_COLORS.COMPLETED.start} stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={GRADIENT_COLORS.COMPLETED.end} stopOpacity={0.02} />
                                    </linearGradient>
                                    <linearGradient id="gradRejected" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={GRADIENT_COLORS.REJECTED.start} stopOpacity={0.2} />
                                        <stop offset="100%" stopColor={GRADIENT_COLORS.REJECTED.end} stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="completed" name="Completed" stroke={STATUS_COLORS.COMPLETED} fill="url(#gradCompleted)" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: STATUS_COLORS.COMPLETED, strokeWidth: 2, stroke: '#fff' }} />
                                <Area type="monotone" dataKey="rejected" name="Rejected" stroke={STATUS_COLORS.REJECTED} fill="url(#gradRejected)" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: STATUS_COLORS.REJECTED, strokeWidth: 2, stroke: '#fff' }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm">
                            No trend data available for this period
                        </div>
                    )}
                </div>

                {/* Status Donut */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                    <div className="mb-4">
                        <h2 className="text-base font-semibold text-gray-900">Status Breakdown</h2>
                        <p className="text-xs text-gray-400 mt-0.5">Completed vs Rejected ratio</p>
                    </div>
                    {d.statusBreakdown.length > 0 ? (
                        <>
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie
                                        data={d.statusBreakdown}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={55}
                                        outerRadius={85}
                                        paddingAngle={4}
                                        dataKey="value"
                                        strokeWidth={0}
                                    >
                                        {d.statusBreakdown.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(val) => fmt(val)} />
                                </PieChart>
                            </ResponsiveContainer>
                            {/* Legend */}
                            <div className="space-y-3 mt-2">
                                {d.statusBreakdown.map((s, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }}></span>
                                            <span className="text-sm text-gray-600">{s.name}</span>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-900">{fmt(s.value)}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm">
                            No status data available
                        </div>
                    )}
                </div>
            </div>

            {/* ─── Item Types Bar Chart ─────────────────────────── */}
            {d.typeBreakdown.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                    <div className="mb-6">
                        <h2 className="text-base font-semibold text-gray-900">Items by Type</h2>
                        <p className="text-xs text-gray-400 mt-0.5">Distribution of order types sold</p>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={d.typeBreakdown} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="value" name="Orders" radius={[8, 8, 0, 0]} maxBarSize={56}>
                                {d.typeBreakdown.map((_, i) => (
                                    <Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* ─── Detailed data table ─────────────────────────── */}
            {d.chartData.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h2 className="text-base font-semibold text-gray-900">Period Details</h2>
                        <p className="text-xs text-gray-400 mt-0.5">Breakdown by {queryParams.groupBy}</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead>
                                <tr className="bg-gray-50/60">
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Period</th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Completed</th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Rejected</th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Success Rate</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {d.chartData.map((row, i) => {
                                    const rate = row.total > 0 ? ((row.completed / row.total) * 100).toFixed(1) : 0;
                                    return (
                                        <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-3.5 text-sm font-medium text-gray-900">{row.period}</td>
                                            <td className="px-6 py-3.5 text-sm text-right">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-medium">
                                                    {fmt(row.completed)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3.5 text-sm text-right">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-red-50 text-red-700 font-medium">
                                                    {fmt(row.rejected)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3.5 text-sm text-right font-semibold text-gray-900">{fmt(row.total)}</td>
                                            <td className="px-6 py-3.5 text-sm text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                                                            style={{ width: `${rate}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-gray-600 font-medium">{rate}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {/* Totals row */}
                            <tfoot>
                                <tr className="bg-gray-50/80 border-t-2 border-gray-200">
                                    <td className="px-6 py-3.5 text-sm font-bold text-gray-900">Total</td>
                                    <td className="px-6 py-3.5 text-sm text-right font-bold text-emerald-700">{fmt(d.totalCompleted)}</td>
                                    <td className="px-6 py-3.5 text-sm text-right font-bold text-red-700">{fmt(d.totalRejected)}</td>
                                    <td className="px-6 py-3.5 text-sm text-right font-bold text-gray-900">{fmt(d.totalOrders)}</td>
                                    <td className="px-6 py-3.5 text-sm text-right font-bold text-gray-900">{d.completionRate}%</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManagementOverview;
